import { useIntl } from "umi";
import { useMemo } from "react";
import { MenuResultType } from "@/types/global";
import { HOSTNAMES } from "../_config/config";
import dataCommonFormat from "@home/utils/dbDataFormat/dataCommonFormat";
import dateTimeFormat from "@home/utils/dbDataFormat/dateTimeFormat";
import { IFormatRule } from "@home/utils/dbDataFormat/_type";
import { FORMAT_RULE_MAP } from "../pages/Agent/config/constant";
import moment from "moment";

export function getLoadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = function () {
      resolve(url);
    };
    img.onerror = function (e) {
      reject(e);
    };
  });
}

// 用于获取中英文本
export const useAigcIntl = (prefix?: string) => {
  const { formatMessage, locale } = useIntl();
  return useMemo(() => {
    const createFormatMessage = (prefix?: string) => {
      const prefixId = prefix ? `aigc_${prefix}` : 'aigc';
      return (id: string, options?: any) => formatMessage({
        id: `${prefixId}_${id}`
      }, options);
    };

    return {
      formatMessage: createFormatMessage(prefix),
      commonFormatMessage: createFormatMessage('common'),
      createFormatMessage,
      locale,
      isEn: locale === 'en-US',
    };
  }, [locale]);
};
export const handleFlatMapRouter = (routerList: any,locale?: string) => {
  const isEn = locale === 'en-US';
  return routerList.map(
    ({
      name_en,
      name_zh,
      id,
      sub_menus,
      icon,
      assembly_url,
      env,
    }: MenuResultType & { name_zh: string; }) => {
      if (HOSTNAMES.includes(window.location.hostname) && name_en === 'Text2SQL') return;
      return {
        title: isEn ? name_en : name_zh,
        path: assembly_url || '',
        id: id || '',
        icon,
        env,
        subMenus:
					sub_menus?.length
					  ? handleFlatMapRouter(sub_menus)
					  : undefined,
      };
    },
  );
};

// 将普通对象转换为FormData形式
export function objectToFormData(object: Object) {
  const formData = new FormData();
  // eslint-disable-next-line no-restricted-syntax
  for (const key in object) {
    // eslint-disable-next-line no-prototype-builtins
    if (object.hasOwnProperty(key) && object[key]) {
      formData.append(key, object[key]);
    }
  }
  return formData;
}

/**
 * 复制到剪切板
 * @param text - 要复制的文本
 * @returns 
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // 方案1: 尝试现代 Clipboard API
  if (navigator?.clipboard) {
    try {
      await navigator?.clipboard?.writeText(text);
      // console.log('复制成功 (Clipboard API)');
      return true;
    } catch (err) {
      console.warn('Clipboard API 失败:', err);
      // 继续尝试备用方案
    }
  }
  
  // 方案2: 使用 document.execCommand 备用方案
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // 防止滚动
    document.body.appendChild(textarea);
    textarea.select();
    
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    
    if (!success) throw new Error('复制失败');
    
    // console.log('复制成功 (execCommand)');
    return true;
  } catch (err) {
    // console.error('所有复制方法均失败:', err);
    return false;
  }
}

// game_name、game_type、granularity 相同的项进行合并。
// 对于每条 metric_value，若其维度字段（date, device, market_name, source, region_name, platform）完全一致，则将其所有指标字段进行合并。
// 若维度不一致，则保留原数据不做合并。
// 指标处理逻辑：若多个相同维度的指标字段都存在值，取最后一个出现的值，而不是相加。

export const mergeData = (inputData:any, data: any) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  // 后端返回的data异常才进行合并（如：inputData返回了两条）；
  if(inputData <=1) return data;

  const result = [];
  const seenMap = new Map(); // 第一层 map：key = game_name + game_type + granularity
  for (const item of data) {
    const { game_name, game_type, granularity, metric_value = [], ...rest } = item || {};
    // 第一层 key
    const key = `${game_name || ''}|${game_type|| ''}|${granularity|| ''}`;
    if (!seenMap.has(key)) {
      // 新建一个分组
      seenMap.set(key, {
        item: {
          game_name,
          game_type,
          granularity,
          metric_value: [],
          ...rest
        },
        metricMap: new Map()
      });
    }

    const group = seenMap.get(key);
    const metricMap = group?.metricMap;

    for (const metric of metric_value) {
      const {
        date,
        device,
        market_name,
        source,
        region_name,
        platform,
        ...metrics
      } = metric;

      // 维度 key
      const dimKey = `${date || ''}|${device || ''}|${market_name || ''}|${source || ''}|${region_name || ''}|${platform || ''}`;

      if (!metricMap.has(dimKey)) {
        // 直接加入
        metricMap.set(dimKey, {
          date,
          device,
          market_name,
          source,
          region_name,
          platform,
          ...metrics
        });
      } else {
        // 已存在，覆盖指标
        const existing = metricMap.get(dimKey);

        for (const [k, v] of Object.entries(metrics)) {
          existing[k] = v; // 覆盖为最新的值
        }
      }
    }
  }

  // 收集最终结果
  for (const { item, metricMap } of seenMap.values()) {
    item.metric_value = Array.from(metricMap.values());
    result.push(item);
  }
  return result;
};

export const findMostOccurrences = (arr: string[], search: string) => {
  let maxCount = 0;
  let topString = "";

  // 转义正则表达式特殊字符
  const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  arr.forEach(str => {
    const count = (str.match(new RegExp(escapedSearch, "gi")) || []).length;
    if (count > maxCount) {
      maxCount = count;
      topString = str;
    }
  });

  return { text: topString, count: maxCount };
};

// agent 数据格式化
export const agentDataFormat = (params: {
  value: any;
  formatRule?: string | IFormatRule;
  emptyValue?: string;
  isDate?: boolean; // 添加这一行
}) => { 
  const { value, formatRule = '', emptyValue, isDate = false  } = params;
  // 明确指定是日期的则走日期格式化逻辑
  if(isDate) {
    // 可以传快捷方式之外的日期格式
    return dateTimeFormat( value, formatRule as string, emptyValue);
  }

  // 否则兜底走通用格式化逻辑（包括日期和数值），只是日期不能传快捷方式之外的格式
  return dataCommonFormat({  value, formatRule, emptyValue});
};

// 走"绝对值阈值"判断 BMK 的格式（公共 BMK 注册多用 value < 1000 判定，导致大额负数不转 KMB），
// 需要在 Y 轴等场景下负数也展示单位时，应使用此列表里的格式
const ABS_BMK_FORMAT_RULES = new Set<string>([
  'intBmk',
  'intBmk0',
  'intBmk00',
  'not0IntBmk0',
  'intBm',
  'intBm0',
  'intBm00',
]);

/**
 * Y 轴格式化（agent 场景）：
 * 公共 BMK 注册（如 not0IntBmk0 => db_int_bmk0_1_0）使用 `value < 1000` 判定单位，
 * 导致 -200000、-400000 这类大额负数被当作"小值"原样输出。
 * 这里在不修改公共注册的前提下，针对相关格式做一层包装：
 *   - 负数先取绝对值走原 formatRule，再补回 "-" 前缀；
 *   - 0 / 正数 / 其他格式（百分比、整数等）保持公共行为。
 */
export const agentYAxisFormat = (params: {
  value: any;
  formatRule?: string | IFormatRule;
  emptyValue?: string;
}) => {
  const { value, formatRule = '', emptyValue } = params;
  const num = Number(value);
  const isAbsBmkRule =
    typeof formatRule === 'string' && ABS_BMK_FORMAT_RULES.has(formatRule);

  if (isAbsBmkRule && Number.isFinite(num) && num < 0) {
    const formatted = dataCommonFormat({
      value: Math.abs(num),
      formatRule,
      emptyValue,
    });
    // 保护：仅当返回的是字符串/数字时再拼负号，避免吞掉空值占位
    if (typeof formatted === 'string' || typeof formatted === 'number') {
      return `-${formatted}`;
    }
    return formatted;
  }

  return dataCommonFormat({ value, formatRule, emptyValue });
};

// 获取映射后的格式化规则
export const getFormatRule = (formatRule: string) => { 
  return FORMAT_RULE_MAP?.[formatRule] || formatRule;
};

/**
 * 时间分类工具函数 - 将聊天列表按时间分类
 * @param chats 聊天列表数组
 * @param bookMarkedChats 收藏的聊天列表（可选）
 * @returns 返回按时间分类的聊天列表
 */
export const classifyChatsByTime = (chats: any[], bookMarkedChats: any[] = []) => {
  const todayItems: any[] = []; // 今日（00:00-24:00）
  const thisWeekItems: any[] = []; // 近7天（昨天到6天前）
  const thisMonthItems: any[] = []; // 近30天（7天前到30天前）
  const earlierItems: any[] = []; // 更早（30天前）

  const now = moment();
  const todayStart = now.clone().startOf('day');
  const todayEnd = todayStart.clone().add(1, 'day');

  const past7DaysStart = todayStart.clone().subtract(6, 'days');
  const past7DaysEnd = todayStart.clone();
  
  const past30DaysStart = todayStart.clone().subtract(30, 'days');
  const past30DaysEnd = past7DaysStart.clone();

  chats.forEach((item) => {
    // 将数据时间视为UTC+8，然后转换为本地时间
    const itemDate = typeof item.timestamp === 'string'
      ? moment.parseZone(`${item.timestamp.replace(/-/g, '/')}+08:00`).local()
      : moment.parseZone(`${item.timestamp}+08:00`).local();

    const checkedIsBookmarkedItem = bookMarkedChats.some(chat => chat.session_id === item.session_id) 
      ? { ...item, bookmarked: true } 
      : item;

    if (itemDate.isSameOrAfter(todayStart.clone()) && itemDate.isBefore(todayEnd.clone())) {
      todayItems.push(checkedIsBookmarkedItem);
    } else if (
      itemDate.isSameOrAfter(past7DaysStart.clone()) &&
      itemDate.isBefore(past7DaysEnd.clone())
    ) {
      thisWeekItems.push(checkedIsBookmarkedItem);
    } else if (
      itemDate.isSameOrAfter(past30DaysStart.clone()) &&
      itemDate.isBefore(past30DaysEnd.clone())
    ) {
      thisMonthItems.push(checkedIsBookmarkedItem);
    } else {
      earlierItems.push(checkedIsBookmarkedItem);
    }
  });

  return {
    todayItems,
    thisWeekItems,
    thisMonthItems,
    earlierItems,
  };
};

// 工具函数：安全访问对象属性
export function safeGet(obj: any, path: string[], defaultValue: any = undefined): any {
  return path.reduce((acc, key) => (acc?.[key] !== undefined ? acc[key] : undefined), obj) ?? defaultValue;
}
// 判断是否为 v2 数据
export function isVersion2(data: any): boolean {
  const version = safeGet(data, ['version']);
  return ['2.0', 2, '2'].includes(String(version));
}

/**
 * 根据会话项推断所属的 template 模式
 * 用于识别当前会话类型（如 dsReport / skill / aiExcel / magicPoster 等）
 * @param sessionItem - 会话项对象
 * @returns 返回会话对应的模板类型字符串，若无法识别则返回空字符串
 */
export const getExpectedTemplate = (sessionItem: any): string => {
  if (!sessionItem) return '';
  if (sessionItem.agent_type === 'ds_agent') return 'dsReport';
  if (sessionItem.agent_type === 'skill_agent' || sessionItem.function?.name === 'chart') return 'skill';
  if (sessionItem.function?.name === 'ai_excel') return 'aiExcel';
  if (sessionItem.function?.name === 'magic_poster') return 'magicPoster';
  if (sessionItem.metadata?.db_args?.enable === true) return 'knowledge';
  return '';
};

/** 模板类型枚举 */
export type TemplateType = 'dsReport' | 'aiExcel' | 'magicPoster' | 'skill' | 'knowledge' | string;

/**
 * 判断模板类型的工具函数
 * @param template - 当前模板
 * @param sessionTemplateMode - 会话模板模式
 * @returns 各模板类型的布尔值
 */
export const getTemplateFlags = (template?: TemplateType, sessionTemplateMode?: string) => {
  const templates = [template, sessionTemplateMode];
  return {
    isDsMode: templates.includes('dsReport'),
    isExcel: templates.includes('aiExcel'),
    isMagicPoster: templates.includes('magicPoster'),
    isSkill: templates.includes('skill'),
    isKnowledge: templates.includes('knowledge'),
  };
};

/** ChatPage / SingleQA 向下游 QAInput 透传的 from 取值 */
export type TChatPageFromPath = 'agent' | 'copilot' | 'dsAgent';

/** resolveChatPageFromPath 实际可能返回的取值（不含 copilot，见函数注释） */
export type TResolvedChatPageFromPath = 'agent' | 'dsAgent';

/**
 * 根据 DS 分栏状态解析 ChatPage 输入区 `from`。
 *
 * | 返回值     | 场景 |
 * |-----------|------|
 * | `agent`   | 未展开分栏，普通对话 |
 * | `dsAgent` | 已展开分栏（gallery / document / 表格 / 海报等） |
 *
 * 此处不返回 `copilot`：该取值在下游同时驱动 AgentModeSelector 的 mention 场景
 * （会多渲染 Skill / 图表按钮）、effectiveTemplate 取值、SubComp 文档卡片禁用预览等，
 * ChatPage 分栏只需要"底部功能键仅 icon"的视觉表现，已由 `.dba-copilot-chat-input`
 * 样式收敛，无需借道 `from`。
 *
 * TODO: 下游链路待重新梳理——当前 `from` 同时驱动 QAInput 样式、AgentModeSelector
 * mention 场景、划词加知识库等，后续应拆为独立语义字段，避免继续扩展 magic string。
 */
export const resolveChatPageFromPath = (
  dsReport?: { show?: boolean; componentType?: string; } | null,
): TResolvedChatPageFromPath => (dsReport?.show ? 'dsAgent' : 'agent');
